// Serializer: konverterar Block[] till email-säker HTML.
// Varje rot-tabell taggas med data-block-type så att parse.ts kan
// rekonstruera blocks vid nästa öppning av designläget.

import {
  Block,
  buildGoogleFontsUrl,
  ButtonBlock,
  CONTENT_MAX_WIDTH_PX,
  DEFAULT_DOC_SETTINGS,
  DividerBlock,
  EmailDocSettings,
  ENVELOPE_BOTTOM_SPACER_PX,
  ENVELOPE_GUTTER_PX,
  ENVELOPE_TOP_SPACER_PX,
  findGoogleFamily,
  HeadingBlock,
  ImageBlock,
  TextBlock,
  TwoColumnsBlock,
} from './types';

// Enkel HTML-escape för plain-text fält.
const escapeHtml = (input: string): string =>
  input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const escapeAttr = (input: string): string => escapeHtml(input);

const headingStyleByLevel = (level: 1 | 2 | 3, fontFamily: string): string => {
  switch (level) {
    case 1:
      return `margin: 0; color: #222222; font-family: ${fontFamily}; font-size: 28px; line-height: 34px;`;
    case 2:
      return `margin: 0; color: #222222; font-family: ${fontFamily}; font-size: 22px; line-height: 28px;`;
    case 3:
    default:
      return `margin: 0; color: #222222; font-family: ${fontFamily}; font-size: 18px; line-height: 22px;`;
  }
};

const renderHeading = (block: HeadingBlock, settings: EmailDocSettings): string => {
  const tag = `h${block.level}`;
  // Font-stack kan innehålla " (t.ex. "Merriweather", Georgia, ...). Inne i ett
  // HTML style-attribut omslutet av " bryts attributet av interna "; vi använder
  // escapeAttr för att HTML-encoda till &quot; — browser decodar tillbaka och
  // CSS-parser:n får tillbaka korrekta citationstecken.
  const safeHeadingFont = escapeAttr(settings.headingFontFamily);
  const style = headingStyleByLevel(block.level, safeHeadingFont);
  const text = escapeHtml(block.text);
  return (
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" ` +
    `data-block-type="heading" data-level="${block.level}" data-align="${block.align}">` +
    `<tr><td align="${block.align}" style="padding: 16px 30px; font-family: ${safeHeadingFont};">` +
    `<${tag} style="${style}">${text}</${tag}>` +
    `</td></tr></table>`
  );
};

const renderText = (block: TextBlock, settings: EmailDocSettings): string => {
  // TinyMCE-output används som-är; vi escapar inte innehållet eftersom det
  // skulle förstöra formattering. TinyMCE saniterar redan input.
  const safeBodyFont = escapeAttr(settings.bodyFontFamily);
  return (
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" ` +
    `data-block-type="text">` +
    `<tr><td style="padding: 12px 30px; color: #666666; font-family: ${safeBodyFont}; ` +
    `font-size: 17px; line-height: 25px;">` +
    `${block.html}` +
    `</td></tr></table>`
  );
};

const renderImage = (block: ImageBlock): string => {
  const widthAttr = block.width ? ` width="${block.width}"` : '';
  const widthStyle = block.width ? `max-width: ${block.width}px;` : 'max-width: 600px;';
  const imgTag =
    `<img src="${escapeAttr(block.src)}" alt="${escapeAttr(block.alt)}"${widthAttr} ` +
    `style="display: block; width: 100%; ${widthStyle} height: auto; border: 0px;" />`;
  const wrapped = block.href ? `<a href="${escapeAttr(block.href)}" target="_blank" rel="noopener noreferrer">${imgTag}</a>` : imgTag;
  const tdPadding = block.fullBleed ? '0' : '12px 30px';
  return (
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" ` +
    `data-block-type="image"${block.href ? ` data-href="${escapeAttr(block.href)}"` : ''}` +
    `${block.fullBleed ? ` data-full-bleed="1"` : ''}>` +
    `<tr><td align="center" style="padding: ${tdPadding};">${wrapped}</td></tr></table>`
  );
};

const renderButton = (block: ButtonBlock, settings: EmailDocSettings): string => {
  const bg = block.bgColor ?? '#222222';
  const fg = block.textColor ?? '#ffffff';
  const safeBodyFont = escapeAttr(settings.bodyFontFamily);
  // Outlook-säker "bulletproof"-knapp: padding ligger på <td> (Outlook
  // ignorerar padding på <a>), <a> får display:block så hela cellen blir
  // klickbar, och label wrappas i <span> med explicit color så Outlook
  // inte applicerar default-länkfärg (blå/svart-understruken).
  return (
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" ` +
    `data-block-type="button" data-align="${block.align}"` +
    `${block.bgColor ? ` data-bg-color="${escapeAttr(block.bgColor)}"` : ''}` +
    `${block.textColor ? ` data-text-color="${escapeAttr(block.textColor)}"` : ''}>` +
    `<tr><td align="${block.align}" style="padding: 16px 30px;">` +
    `<table border="0" cellpadding="0" cellspacing="0"><tr>` +
    `<td bgcolor="${escapeAttr(bg)}" align="center" ` +
    `style="background-color: ${escapeAttr(bg)}; border-radius: 4px; padding: 12px 28px;">` +
    `<a href="${escapeAttr(block.href)}" target="_blank" rel="noopener noreferrer" ` +
    `style="color: ${escapeAttr(fg)}; font-family: ${safeBodyFont}; font-size: 15px; ` +
    `font-weight: 600; text-decoration: none; display: block;">` +
    `<span style="color: ${escapeAttr(fg)}; text-decoration: none;">${escapeHtml(block.label)}</span>` +
    `</a>` +
    `</td></tr></table>` +
    `</td></tr></table>`
  );
};

const renderDivider = (block: DividerBlock): string => {
  const color = block.color ?? '#dddddd';
  return (
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" ` +
    `data-block-type="divider"${block.color ? ` data-color="${escapeAttr(block.color)}"` : ''}>` +
    `<tr><td style="padding: 12px 30px;">` +
    `<hr style="border: 0; border-top: 1px solid ${escapeAttr(color)}; margin: 0;" />` +
    `</td></tr></table>`
  );
};

const renderTwoColumns = (block: TwoColumnsBlock, settings: EmailDocSettings): string => {
  // Inre kolumnerna serialiseras rekursivt. För round-trip-stabilitet
  // wrappar vi varje kolumn med data-column-attribut.
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const leftHtml = block.left.map((b) => renderBlock(b, settings)).join('');
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const rightHtml = block.right.map((b) => renderBlock(b, settings)).join('');
  return (
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" ` +
    `data-block-type="twoColumns">` +
    `<tr><td style="padding: 12px 30px;">` +
    `<table border="0" cellpadding="0" cellspacing="0" width="100%"><tr>` +
    `<td valign="top" width="50%" style="padding-right: 10px;" data-column="left">${leftHtml}</td>` +
    `<td valign="top" width="50%" style="padding-left: 10px;" data-column="right">${rightHtml}</td>` +
    `</tr></table>` +
    `</td></tr></table>`
  );
};

// Renderar ett enskilt block. Exporteras för testbarhet och rekursion.
// Tar settings för att kunna applicera typsnittsval på heading/text/button.
export const renderBlock = (block: Block, settings: EmailDocSettings = DEFAULT_DOC_SETTINGS): string => {
  switch (block.type) {
    case 'heading':
      return renderHeading(block, settings);
    case 'text':
      return renderText(block, settings);
    case 'image':
      return renderImage(block);
    case 'button':
      return renderButton(block, settings);
    case 'divider':
      return renderDivider(block);
    case 'twoColumns':
      return renderTwoColumns(block, settings);
    default: {
      const exhaustive: never = block;
      throw new Error(`Okänd blocktyp vid rendering: ${JSON.stringify(exhaustive)}`);
    }
  }
};

// Wrappar ett enskilt blocks HTML i envelope-rad: body-bg-cell → centrerad
// 600px content-tabell → content-bg-cell. Matchar mönstret i system-mallarna.
const wrapInEnvelopeRow = (blockHtml: string, settings: EmailDocSettings): string => {
  const body = escapeAttr(settings.bodyBgColor);
  const content = escapeAttr(settings.contentBgColor);
  return (
    `<tr>` +
    `<td bgcolor="${body}" align="center" style="padding: 0px ${ENVELOPE_GUTTER_PX}px 0px ${ENVELOPE_GUTTER_PX}px;">` +
    `<table border="0" cellpadding="0" cellspacing="0" width="100%" style="max-width: ${CONTENT_MAX_WIDTH_PX}px;">` +
    `<tr>` +
    `<td bgcolor="${content}" align="left" valign="top" style="padding: 0;">` +
    `${blockHtml}` +
    `</td>` +
    `</tr>` +
    `</table>` +
    `</td>` +
    `</tr>`
  );
};

// Tom rad med enbart body-bg som ger vertikal luft ovanför första blocket
// eller under sista. Saknar data-block-type så parser ignorerar dem.
const renderSpacerRow = (heightPx: number, settings: EmailDocSettings): string => {
  const body = escapeAttr(settings.bodyBgColor);
  return `<tr><td bgcolor="${body}" style="height: ${heightPx}px; line-height: ${heightPx}px; font-size: 0;">&nbsp;</td></tr>`;
};

// Serialiserar en hel block-lista till HTML. Hela utgåvan slås in i en yttre
// envelope-tabell med en topp/botten-spacer + en rad per block — samma mönster
// som system-mallarna använder och säkerställer att slutmailet ser ut som i editorn.
//
// Den yttre tabellen taggas med data-email-doc + bg-attribut så att parse.ts
// kan rekonstruera doc-settings vid nästa öppning av designläget.
export const renderBlocks = (blocks: Block[], settings: EmailDocSettings = DEFAULT_DOC_SETTINGS): string => {
  const body = escapeAttr(settings.bodyBgColor);
  const content = escapeAttr(settings.contentBgColor);
  const rows = blocks.map((b) => wrapInEnvelopeRow(renderBlock(b, settings), settings)).join('\n');

  // Bygg <link> till Google Fonts för de fonter som faktiskt används. Vi
  // dubblerar med <style>@import för maximal kompatibilitet — vissa kontexter
  // (TinyMCE-iframe, äldre mail-klienter) hanterar bara den ena varianten.
  // Båda har klass-markörer så consumers kan flytta dem till <head>.
  const googleUrl = buildGoogleFontsUrl([findGoogleFamily(settings.headingFontFamily), findGoogleFamily(settings.bodyFontFamily)]);
  const fontImport = googleUrl
    ? `<link class="fs-fonts-link" rel="stylesheet" href="${googleUrl}" />\n<style class="fs-fonts-import">@import url('${googleUrl}');</style>\n`
    : '';

  return (
    `${fontImport}` +
    `<table border="0" cellpadding="0" cellspacing="0" width="100%" ` +
    `data-email-doc="1" data-body-bg="${body}" data-content-bg="${content}" ` +
    `data-heading-font="${escapeAttr(settings.headingFontFamily)}" data-body-font="${escapeAttr(settings.bodyFontFamily)}">\n` +
    `${renderSpacerRow(ENVELOPE_TOP_SPACER_PX, settings)}\n` +
    `${rows}\n` +
    `${renderSpacerRow(ENVELOPE_BOTTOM_SPACER_PX, settings)}\n` +
    `</table>`
  );
};
